import React, { useState, useEffect } from "react";
import { Link, useParams } from 'react-router-dom';
import {
  Checkbox,
  Tabs,
  Tab,
  Box,
  Button,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  FormControlLabel
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getVal, setBool, getTableData } from "../../helpers/process_morningstar.js";

import { DownloadInvoiceButton } from '../Pdf.js';

function formatMarketCap(value) {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)} Trillion`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(2)} Billion`;
  } else if (value >= 100) {
    return `$${(value).toFixed(2)} Million`;
  } else {
    return `$${value.toFixed(2)}`;
  }
}


function Header({graphData, pdfImage, pdfDescription}) {
    const { ticker } = useParams();
    const [companyDetail, setCompanyDetail] = useState([])
    const [closePrice, setClosePrice] = useState([])
    const [security, setSecurity] = useState([])
    const [parameters, setParameters] = useState([])
    const [generalProfile, setGeneralProfile] = useState([])
    const [currency, setCurrency] = useState()

    useEffect(() => {
        const fetchData = async () => {
            const companyTable = await getTableData("Company")
            setCompanyDetail(companyTable[0])

            const stockTable = await getTableData("PVTwoYears")
            setClosePrice(stockTable[3])

            const securityTable = await getTableData("Security")
            setSecurity(securityTable[0])

            const parametersTable = await getTableData("Parameters")
            setParameters(parametersTable)
            setCurrency(parametersTable[7][ticker.toUpperCase()])

            const generalProfileTable = await getTableData("General Profile")
            setGeneralProfile(generalProfileTable[0])
        }


    fetchData()
    }, [])

    const companyName = companyDetail['StandardName']


    const dateKeys = Object.keys(closePrice).filter(key => /^\d{4}-\d{2}-\d{2}$/.test(key));
    dateKeys.sort((a, b) => new Date(a) - new Date(b));

    const latestDate = dateKeys[dateKeys.length - 1];
    const secondDate = dateKeys[dateKeys.length - 2];
    const latestPrice = Math.round(closePrice[latestDate] * 100) / 100;
    const secondPrice = Math.round(closePrice[secondDate] * 100) / 100

    const priceChange = Math.round((latestPrice - secondPrice) * 100) / 100
    const percentChange = Math.round((priceChange / secondPrice) * 100 * 100) / 100

    const marketCap = graphData[graphData.length - 1]['marketCap']

    const sectorName = companyDetail['SectorName']

    const primaryExchange = security['ExchangeId']

    const companyWebsite = generalProfile['HeadquarterHomepage']
    const companyLocation = generalProfile['HeadquarterCity'] + ', ' + generalProfile['HeadquarterProvince'] + ', ' + generalProfile['HeadquarterCountry']

    const headerData = {
      companyName,
      ticker,
      latestPrice,
      priceChange,
      percentChange,
      latestDate,
      marketCap,
      sectorName,
      currency,
      primaryExchange,
      companyWebsite,
      companyLocation,
      pdfImage,
      pdfDescription
    };

  return (
    <Grid 
    container 
    spacing={1} 
    alignItems="flex-start" 
    sx={{ mb: 1 }}
  >

    {/* TITLE */}
    <Grid item xs={12} sx={{ paddingRight: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between", 
          width: "100%",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            m: 0,
            fontWeight: "bold",
            color: "#002060"
          }}
        >
          {companyName} ({ticker.toUpperCase()})
        </Typography>

        {pdfImage && (
          <DownloadInvoiceButton
            chartImage={pdfImage}
            description={pdfDescription}
            headerData={headerData}
          />
        )}
      </Box>
    </Grid>

    {/* LEFT COLUMN */}
    <Grid item xs={12} sm={6} md={3.5}>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", color: "#002060" }}
        >
          ${latestPrice}{" "}
          <Box component="span" sx={{ fontSize: "0.75rem", color: "black" }}>
            <Box
              component="span"
              sx={{
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: percentChange >= 0 ? "green" : "red",
              }}
            >
              {percentChange >= 0 ? `+${priceChange}` : priceChange} (
              {percentChange >= 0 ? `+${percentChange}%` : `${percentChange}%`})
            </Box>
            {" "}as of {latestDate}
          </Box>
        </Typography>

        <Typography variant="subtitle2" sx={{ color: "black" }}>
          Market Cap: {formatMarketCap(marketCap)}
        </Typography>

        <Typography variant="subtitle2" sx={{ color: "black" }}>
          Sector: {sectorName}
        </Typography>
      </Box>
    </Grid>

    {/* MIDDLE COLUMN */}
    <Grid 
      item 
      xs={12} 
      sm={6} 
      md={3}
      sx={{
        textAlign: { xs: "left", md: "left" },
        pl: { xs: 0, sm: 2, md: 6 }
      }}
    >
      <Typography variant="subtitle2" sx={{ color: "black" }}>
        Financials Reported In: {currency}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: "black" }}>
        Stock Chart In: {currency}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: "black" }}>
        Primary Exchange: {primaryExchange}
      </Typography>
    </Grid>

    {/* COLUMN 3 */}
    <Grid 
      item 
      xs={12} 
      sm={6} 
      md={3}
      sx={{
        textAlign: { xs: "left", md: "left" },
        pl: { xs: 0, sm: 2, md: 6 }
      }}
    >
      <Link
        to={companyWebsite}
        target="_blank" rel="noopener noreferrer"
      >
        {companyWebsite}
      </Link>

      <Typography variant="subtitle2" sx={{ color: "black" }}>
        {companyLocation}
      </Typography>
    </Grid>

    {/* RIGHT COLUMN */}
    <Grid 
      item 
      xs={12} 
      sm={6} 
      md={2.5}
      sx={{
        textAlign: { xs: "left", md: "right" },
        pl: { xs: 0, sm: 2, md: 6 },
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: "-8px" }}>
        Powered By:  
        <img
          src="/fsa_logo.png"
          alt="fsa logo"
          style={{
            width: "auto",
            height: "38px",
            marginLeft: "2px",
            verticalAlign: "middle"
          }}
        />
      </Typography>

      <Link
        to="https://fsavaluation.com/"
        target="_blank" 
        rel="noopener noreferrer"
      >
        www.fsavaluation.com
      </Link>

      <Typography variant="subtitle2">
        416 453-5875
      </Typography>

    </Grid>
    
  </Grid>

  );
}

export default Header;
